import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { Avatar } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { FormCard } from '@lmc/ui-kit'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import dayjs from 'dayjs'
import { AdministrationStatus, StockRecord } from '../../types'
import { RevisionHistorySection } from '../RevisionHistory/RevisionHistorySection'

type AdministrationRecordDetailContentProps = {
  record: StockRecord
}

export const AdministrationRecordDetailContent = ({
  record
}: AdministrationRecordDetailContentProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const adminData = record.administration
  if (!adminData) {
    return null
  }

  // Extract data from administration object
  const dose = adminData.dose || adminData.doseQuantity || 0
  const unit = record.unit || ''
  const administeredText = `${dose} ${unit}`

  // Get recorded by information
  const recordedBy = adminData.recordedBy
  const signedByName =
    typeof recordedBy.name === 'object'
      ? `${recordedBy.name.first} ${recordedBy.name.last}`
      : recordedBy.name || ''

  const timeLogged = dayjs(record.date || adminData.administeredTime).format(
    'DD-MM-YYYY HH:mm'
  )

  // Get comment/note
  const comment = adminData.note || record.notes || ''
  const commentText = Array.isArray(comment) ? comment.join(', ') : comment

  // Scheduled for time
  const scheduledFor = adminData.startTime
    ? dayjs(adminData.startTime).format('DD-MM-YYYY HH:mm')
    : '-'

  // Determine if stock was removed based on dose quantity
  const doseQuantity = Number(adminData.doseQuantity || dose || 0)
  const stockWasRemoved = doseQuantity > 0

  let stockChangedText: string
  if (stockWasRemoved) {
    const unitText = unit ? ` ${unit}` : ''
    stockChangedText = `${t('YES')}, removed ${doseQuantity}${unitText} from stock`
  } else {
    stockChangedText = t('NO')
  }

  return (
    <SectionContainer>
      <FormCard level={2}>
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>
              {adminData.administrationStatus === AdministrationStatus.GIVEN
                ? t('HOW_MANY_ADMINISTERED')
                : t('HOW_MANY_REMOVED')}
            </StyledLabel>
            <Text>{administeredText}</Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('HAS_STOCK_AMOUNT_CHANGED')}</StyledLabel>
            <Text>{stockChangedText}</Text>
          </InfoSection>
        </InfoRow>
        <RowDivider />
        <InfoRow>
          <FullWidthSection>
            <StyledLabel level={2}>{t('COMMENT')}</StyledLabel>
            <Text>{commentText || '-'}</Text>
          </FullWidthSection>
        </InfoRow>
        <RowDivider />
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('TIME_RECORDED')}</StyledLabel>
            <Text>{timeLogged}</Text>
          </InfoSection>
          {record.witnessed &&
            record.witnesses &&
            record.witnesses.length > 0 && (
              <InfoSection>
                <StyledLabel level={2}>{t('WITNESSED_BY')}</StyledLabel>
                <WitnessesContainer>
                  {record.witnesses.map((witness: any, index: number) => {
                    const witnessName =
                      typeof witness.name === 'object'
                        ? `${witness.name.first} ${witness.name.last}`
                        : witness.name || '-'
                    return (
                      <WitnessItem key={witness.userId || index}>
                        <Avatar
                          url={witness.picture as unknown as string}
                          size={16}
                        />
                        <Text weight="bold">{witnessName}</Text>
                      </WitnessItem>
                    )
                  })}
                </WitnessesContainer>
              </InfoSection>
            )}
          <InfoSection>
            <StyledLabel level={2}>{t('SIGNED_BY')}</StyledLabel>
            <SignedByContainer>
              <Avatar url={recordedBy.picture as unknown as string} size={16} />
              <Text weight="bold">{signedByName || '-'}</Text>
            </SignedByContainer>
          </InfoSection>
        </InfoRow>
      </FormCard>
      {record.changelog && (
        <RevisionHistorySection changelog={record.changelog} />
      )}
    </SectionContainer>
  )
}

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 184px;
`

const FullWidthSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const RowDivider = styled(Divider)`
  margin: -8px 0;
`

const SignedByContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const WitnessesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const WitnessItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
